import React, { useState, useEffect } from "react";
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    useMediaQuery,
    useTheme,
} from "@mui/material";

const headCells = [
    { id: 'broker', label: 'Broker', align: 'left', width: '20%' },
    {
        id: 'avanceMetaTrimestral', label: (
            <>
                Avance Meta<br />
                Trimestral
            </>
        ), align: 'right', width: '14%'
    },
    {
        id: 'cierresMensual', label: (
            <>
                Cierres<br />
                Mensual
            </>
        ), align: 'right', width: '14%'
    },
    { id: 'nStock', label: 'N° Stock', align: 'right', width: '14%' },
    { id: 'visualizaciones', label: 'Visualizaciones', align: 'right', width: '14%' },
];

export const MainTable = ({ data = [], userData, selectedBrokerCorreo, setSelectedBrokerCorreo }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [currentMetricIndex, setCurrentMetricIndex] = useState(0);

    const isAdmin = userData && userData.tipoCuenta === 0;

    useEffect(() => {
        if (!isMobile) return;
        const timer = setTimeout(() => {
            setCurrentMetricIndex((prev) => (prev + 1) % 3);
        }, 5000);
        return () => clearTimeout(timer);
    }, [currentMetricIndex, isMobile]);

    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);

    const minSwipeDistance = 50;

    const onTouchStart = (e) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
    };

    const onTouchMove = (e) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const onTouchEnd = () => {
        if (!touchStart || !touchEnd) return;
        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > minSwipeDistance;
        const isRightSwipe = distance < -minSwipeDistance;

        if (isLeftSwipe) {
            setCurrentMetricIndex((prev) => (prev + 1) % 3);
        } else if (isRightSwipe) {
            setCurrentMetricIndex((prev) => (prev === 0 ? 2 : prev - 1));
        }
    };

    const getAvance = (row) => row.ingreso_Trimestral / row.meta_Trimestral * 100;
    const getCierres = (row) => row.n_Cierres_Mensual ?? 0;
    const getStock = (row) => row.n_Stocks ?? 0;
    const getVistas = (row) => row.visualizaciones_Propiedades ?? 0;
    const getBroker = (row) => row.nombre_Broker ?? "-";

    const sortedData = [...data].sort((a, b) => getAvance(b) - getAvance(a));

    const rotatingColumns = [
        {
            id: 'cierresMensual', label: (
                <>
                    Cierres<br />
                    Mensual
                </>
            ), align: 'right', width: '30%', getValue: getCierres
        },
        { id: 'nStock', label: 'N° Stock', align: 'right', width: '30%', getValue: getStock },
        { id: 'visualizaciones', label: 'Visualizaciones', align: 'right', width: '30%', getValue: getVistas },
    ];

    const activeRotatingCol = rotatingColumns[currentMetricIndex];

    const currentHeadCells = isMobile ? [
        { id: 'broker', label: 'Broker', align: 'left', width: '40%' },
        {
            id: 'avanceMetaTrimestral', label: (
                <>
                    Avance Meta<br />
                    Trimestral
                </>
            ), align: 'right', width: '30%'
        },
        { ...activeRotatingCol }
    ] : headCells;

    return (
        <Box
            sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, overflow: 'hidden' }}
            onTouchStart={isMobile ? onTouchStart : undefined}
            onTouchMove={isMobile ? onTouchMove : undefined}
            onTouchEnd={isMobile ? onTouchEnd : undefined}
        >
            <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    mb: 1,
                    borderRadius: 2,
                    border: "1px solid #e0e0e0",
                    overflow: 'hidden',
                    mb: '0px !important'

                }}
            >
                {/* Header Table */}
                <Box sx={{ pr: '8px', borderBottom: '2px solid #ccc' }}> {/* Account for scrollbar space */}
                    <Table
                        size="small"
                        sx={{
                            tableLayout: 'fixed',
                            width: '100%',
                        }}
                    >
                        <TableHead>
                            <TableRow>
                                {currentHeadCells.map((headCell) => {
                                    const isFixed = headCell.id === 'broker' || headCell.id === 'avanceMetaTrimestral';
                                    return (
                                        <TableCell
                                            key={headCell.id}
                                            align={headCell.align}
                                            sx={{
                                                width: headCell.width,
                                                fontWeight: isFixed ? "900" : "normal",
                                                color: isFixed ? "text.primary" : "text.secondary",
                                                borderBottom: "none",
                                                py: 1.5,
                                                fontSize: isMobile ? '0.75rem' : '0.875rem'
                                            }}
                                        >
                                            {headCell.label}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        </TableHead>
                    </Table>
                </Box>

                {/* Body Table Container */}
                <Box
                    sx={{
                        flexGrow: 1,
                        overflowY: 'scroll',
                        '&::-webkit-scrollbar': {
                            width: '8px',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            backgroundColor: '#ccc',
                            borderRadius: '4px',
                        },
                    }}
                >
                    <Table
                        size="small"
                        sx={{
                            tableLayout: 'fixed',
                            width: '100%',
                        }}
                    >
                        <TableBody>
                            {sortedData.map((row, index) => {
                                const isSelected = selectedBrokerCorreo === row.correo;
                                return (
                                    <TableRow
                                        key={row.id || index}
                                        onClick={() => {
                                            if (isAdmin && setSelectedBrokerCorreo) {
                                                if (isSelected) {
                                                    setSelectedBrokerCorreo(null);
                                                } else {
                                                    setSelectedBrokerCorreo(row.correo);
                                                }
                                            }
                                        }}
                                        sx={{
                                            cursor: isAdmin ? "pointer" : "default",
                                            bgcolor: isSelected ? "rgba(12, 194, 61, 0.47)" : undefined,
                                            "&:nth-of-type(odd)": { bgcolor: isSelected ? "rgba(0, 166, 43, 0.1)" : "#f9f9f9" },
                                            "& td": { borderBottom: "none" },
                                            "&:hover": isAdmin ? { bgcolor: "rgba(0, 166, 44, 0.56) !important" } : {},
                                        }}
                                    >
                                        <TableCell sx={{ width: isMobile ? '40%' : '20%', py: 1, fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
                                            {getBroker(row)}
                                        </TableCell>
                                        <TableCell align="right" sx={{ width: isMobile ? '30%' : '14%', py: 1, fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
                                            {getAvance(row).toFixed(1)}%
                                        </TableCell>
                                        {isMobile ? (
                                            <TableCell align="right" sx={{ width: '30%', py: 1, fontSize: '0.75rem' }}>
                                                {activeRotatingCol.getValue(row)}
                                            </TableCell>
                                        ) : (
                                            <>
                                                <TableCell align="right" sx={{ width: '14%', py: 1 }}>{getCierres(row)}</TableCell>
                                                <TableCell align="right" sx={{ width: '14%', py: 1 }}>{getStock(row)}</TableCell>
                                                <TableCell align="right" sx={{ width: '14%', py: 1 }}>{getVistas(row)}</TableCell>
                                            </>
                                        )}
                                    </TableRow>
                                )
                            })}
                        </TableBody>
                    </Table>
                </Box>
            </TableContainer>
            {isMobile && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1, pb: 1, gap: 1 }}>
                    {rotatingColumns.map((_, idx) => (
                        <Box
                            key={idx}
                            onClick={() => setCurrentMetricIndex(idx)}
                            sx={{
                                width: idx === currentMetricIndex ? 16 : 8,
                                height: 8,
                                borderRadius: 4,
                                bgcolor: idx === currentMetricIndex ? '#00a62b' : '#ccc',
                                transition: 'all 0.3s ease',
                                cursor: 'pointer'
                            }}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
};
